/*
  This utility collection is meant to be the single-source-of-truth
  for logic related to blueprint/widget data structure as well as handle version
  differences.  Ideally, if the blueprint changes, we want to only update these
  utilities and not need to update ACE-Editor logic in other components.
  These single-responsibility functions also help with DRYness.

  Remember: a 'blueprint' has a 'widgetGroup' key that is an array of 'widget' arrays that must contain at
  at least one widget, ie

  blueprint.widgetGroups = [
    {
      widgets: [ w1, w2, w3 ],
      properties: {}
    },
    {
      widgets: [ w4, w5, w6 ],
      properties: {}
    }
  ]
*/

/********************************** LOOKUP FUNCTIONS (all return booleans) **********************************/

// LOOKUP, public - to determine if a WIDGET can either be edit single vs all
//
// any widget that is not codesnippet can be all editable
// @return {bool}
function isValidForAllEdit(widget) {
  return !(!widget || widget.type === 'codesnippet')
}


// LOOKUP, public - to determine if a non-data image WIDGET can either be edit single vs all
// - only company logo and cta image button apply currently
//
// @return {bool}
function isNotDataImageRPButValidForAllEdit(widget) {
  return (
    widget.meta.toolbox.label.toLowerCase() == 'company logo' ||
    widget.meta.toolbox.label.toLowerCase() == 'cta image button'
  )
}

/********************************** CREATE FUNCTIONS **********************************/

// FACTORY, public - Return a JSON object representing a rich placeholder for href key
// of widgets
//
// @return richplaceholder {object}
function generateRichPlaceholderForHref(defaultValue = null) {
  return {
    displayName: 'Webpage Link',
    name: 'productLink',
    type: 'url',
    truncate: null,
    defaultValue,
    inventoryIndex: 0,
    codeType: 'attribute',
  }
}

/********************************** GET FUNCTIONS **********************************/

// GET, public - Get RPs from HTML template
//
// @return widget {object}
/*
  scenarios to be considered in parsing the rich placeholders
  1: {{bla}}div{{bla2}}div2
  2: div
  3: {{bla}}
  4: nested {{ {{ {{ rp }} text}} text2}}div
  5: nested {{ {{ {{ rp }} }} }}div
  6: nested {{ {{ {{ rp }}}}}}div
  7: code { bla { more }}
  8: code {{ bla } text }
  Due to these scenarios we opted to use a pointer based string scan as implemented below. This method has been verified for scenario 1,2,3.
  Others we can not verify in the given amount of time

  This function was ripped and altered from CSR

  PLO June 2021: This is overkill, most of these use cases do not and will never occur.
*/
function getRPsFromHtmlTemplate(placeholderString) {
  const rps = []
  const placeholderStringFragments = []
  let lastBrackets = placeholderString.lastIndexOf('{{')
  if (lastBrackets > -1) {
    let bracketPos = 0
    while (bracketPos < lastBrackets) {
      let bracketPosNext = placeholderString.indexOf('{{', bracketPos)
      let closingBracketPos = placeholderString.indexOf('}}', bracketPosNext)
      // Reached the end of possible rph (no more {{ or }} in string) (at least scenario 7 & 8)
      if (bracketPosNext == -1 || closingBracketPos == -1) {
        break
      }
      // Get possible rph including { }
      let rpString = placeholderString.substring(bracketPosNext + 1, closingBracketPos + 1)
      try {
        // parse placeholders and add to collection
        rps.push(JSON.parse(rpString))
      } catch (err) {
        // There was an error parsing an RP from template html... ignore and move on.  We won't
        // offer up that as an RP if someone miscoded their template.
      }
      placeholderStringFragments.push(placeholderString.substring(bracketPos, closingBracketPos + 2))
      bracketPos = closingBracketPos + 2
    }
    placeholderStringFragments.push(placeholderString.substring(bracketPos))
    // combine fragments back into one string for each scene
    placeholderString = placeholderStringFragments.join('')
  }

  return rps
}

function getRPsSquareBracketFromHtmlTemplate(placeholderString) {
  const rpSquareBrackets = []
  const placeholderStringFragments = []
  let lastBrackets = placeholderString.lastIndexOf('[[')
  if (lastBrackets > -1) {
    let bracketPos = 0
    while (bracketPos < lastBrackets) {
      let bracketPosNext = placeholderString.indexOf('[[', bracketPos)
      let closingBracketPos = placeholderString.indexOf(']]', bracketPosNext)
      // Reached the end of possible rph (no more {{ or }} in string) (at least scenario 7 & 8)
      if (bracketPosNext == -1 || closingBracketPos == -1) {
        break
      }
      // Get possible rph including { }
      let rpString = placeholderString.substring(bracketPosNext + 2, closingBracketPos)
      try {
        // parse placeholders and add to collection
        rpSquareBrackets.push(JSON.parse(`{${rpString}}`))
      } catch (err) {
        // There was an error parsing an RP from template html... ignore and move on.  We won't
        // offer up that as an RP if someone miscoded their template.
      }
      placeholderStringFragments.push(placeholderString.substring(bracketPos, closingBracketPos + 2))
      bracketPos = closingBracketPos + 2
    }
    placeholderStringFragments.push(placeholderString.substring(bracketPos))
    // combine fragments back into one string for each scene
    placeholderString = placeholderStringFragments.join('')
  }

  return rpSquareBrackets
}

/********************************** SET FUNCTIONS **********************************/

// SET, public - setter for converting Company Logo and CTA Image Button widget value content back into a RP object
//
// @return undefined
function setLogoOrCTAImageWidgetRPContent(widget) {
  widget.value.content = {
    ...CommonWidgets.widgets.find(w => w.meta.toolbox.label.toLowerCase() === widget.meta.toolbox.label.toLowerCase())
      .value.content,
    defaultValue: widget.value.content,
  }
}

/********************************** Finally Export **********************************/

export default {
  lookup: {
    isNotDataImageRPButValidForAllEdit,
    isValidForAllEdit,
  },
  create: {
    richPlaceholderHref: generateRichPlaceholderForHref,
  },
  get: {
    RPsSquareBracketFromHtmlTemplate: getRPsSquareBracketFromHtmlTemplate,
    RPsFromHtmlTemplate: getRPsFromHtmlTemplate,
  },
  set: {
    logoOrCTAImageWidgetRPContent: setLogoOrCTAImageWidgetRPContent,
  },
}
